import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IContract } from '@components/DeviceActionModal/types';
import useSwr from '@hooks/useSwr';
import {
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Radio,
  Row,
  Select,
  Input,
  message,
  Upload,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import { InboxOutlined } from '@ant-design/icons';
import i18next from 'i18next';

import * as S from './styles';

const DETAILS_MAX_LENGTH = 4000;

const CONTESTACAO_FATURA_KEY = 'CONTESTACAO FATURA';
const EQUIPAMENTO_KEY = 'EQUIPAMENTO NAO ENTREGUE';
const BLOQUEIO_KEYS = ['DESBLOQUEIO EM CONFIANCA', 'SUSPENSAO'];
const isBloqueioKey = (desc: string) =>
  BLOQUEIO_KEYS.some(k => desc === k || desc.startsWith(k));

type TemplateType = 'equipamento' | 'bloqueio' | 'contestacaoFatura' | null;

const FORMA_DEVOLUCAO_OPTIONS = [
  {
    value: 'ajuste_parcial_total',
    label:
      'Ajuste parcial ou total da fatura paga (crédito na próxima fatura e retorno de confirmação de ajuste para o cliente)',
  },
  {
    value: 'ajuste_parcial',
    label: 'Ajuste parcial da fatura (envio do PDF da fatura junto com boleto)',
  },
  {
    value: 'ajuste_total',
    label:
      'Ajuste total da fatura (retorno de confirmação do ajuste para o cliente)',
  },
];

const { TextArea } = Input;
const { Text } = Typography;

type SelectionOptions = {
  value: string;
  label: string;
};

type TypeNames =
  | 'srType1'
  | 'srType2'
  | 'srType3'
  | 'srType4'
  | 'complaintProblem'
  | 'complaintReason';

interface SrTypes {
  srType1?: string;
  srType2?: string;
  srType3?: string;
  srType4?: string;
  complaintProblem?: string;
  complaintReason?: string;
}

interface EquipamentoFields {
  eqQuantidade?: string;
  eqDataPedido?: string;
  eqDescricao?: string;
}

interface BloqueioFields {
  blLinhasContrato?: string;
  blDescricao?: string;
}

interface ContestacaoFaturaFields {
  cfRazaoSocial?: string;
  cfCNPJ?: string;
  cfFatura?: string;
  cfContrato?: string;
  cfProduto?: string;
  cfMotivo?: string;
  cfValor?: string;
  cfFormaDevolucao?: string;
  cfRetornoNome?: string;
  cfRetornoTelefone?: string;
  cfRetornoEmail?: string;
  cfRetornoConsultor?: string;
}

interface FormValues
  extends SrTypes,
    EquipamentoFields,
    BloqueioFields,
    ContestacaoFaturaFields {
  assetNumber: string;
  details: string;
  uploaded: { file: File; fileList: File[] };
}

interface TreeProps {
  level: number;
  type: string;
  id: string;
  description: string;
  next: TreeProps[] | null;
}

interface NewServicePayload {
  assetId: string;
  worklogText: string;
  srTypeRequest: SrTypes;
}

interface NewServiceRequestProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const getWorklogCounterType = (
  length: number,
  max: number,
): 'danger' | 'warning' | 'secondary' => {
  if (length > max) return 'danger';
  if (length > max * 0.9) return 'warning';
  return 'secondary';
};

const WorklogCounter = ({
  currentLength,
  maxLength,
}: {
  currentLength: number;
  maxLength: number;
}) => (
  <Text type={getWorklogCounterType(currentLength, maxLength)}>
    {currentLength}
    {' / '}
    {maxLength}
  </Text>
);

const NewServiceRequest = ({
  open,
  onClose,
  onSuccess,
}: NewServiceRequestProps) => {
  const { t } = useTranslation('service-requests');
  const [form] = Form.useForm();
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const selectedValues = Form.useWatch<FormValues>([], form);

  const { data: typesTree } = useSwr<{ roots: TreeProps[] } | undefined>(
    open ? '/service-proxy/service/request/getSrTypesTree' : null,
  );

  const { data: contracts } = useSwr<IContract[]>(
    open ? '/service-proxy/contracts' : null,
  );

  const createServiceRequest = useCallback(
    (payload: NewServicePayload) =>
      api.post(
        '/service-proxy/service/request/createServiceRequestGeneric',
        payload,
      ),
    [],
  );

  const sendFile = useCallback(
    (formData: FormData, objectId: string, assetNumber: string) =>
      api.post(`/service-proxy/service/request/fileUpload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          objectId,
          assetId: assetNumber,
        },
      }),
    [],
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ planName, assetNumber, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
          }))
        : [],
    [contracts],
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const handleCascadeFormReset = useCallback(
    (changedField: TypeNames, changedValues?: SrTypes) => {
      const fields = [
        'srType1',
        'srType2',
        'srType3',
        'srType4',
        'complaintProblem',
        'complaintReason',
      ] as const;
      const index = fields.indexOf(changedField);

      if (index === -1) return;

      const updatedValues = {
        srType1: selectedValues?.srType1,
        srType2: selectedValues?.srType2,
        srType3: selectedValues?.srType3,
        srType4: selectedValues?.srType4,
        complaintProblem: selectedValues?.complaintProblem,
        complaintReason: selectedValues?.complaintReason,
      };

      fields.slice(index + 1).forEach(field => {
        updatedValues[field] = undefined;
      });

      form.setFieldsValue({ ...updatedValues, ...changedValues });
    },
    [form, selectedValues],
  );

  const renderInputEl = useCallback(
    (
      type: 'assetNumber' | TypeNames,
      options: SelectionOptions[] | undefined,
      onChange?: (value: string) => void,
    ) => {
      if (!options) {
        return <></>;
      }

      const onlyOneOption = options?.length === 1;

      if (onlyOneOption) {
        form.setFieldValue(type, options[0]?.value);
      }

      const renderSelect =
        type === 'assetNumber' ||
        options.length > 3 ||
        ['complaintProblem', 'complaintReason'].includes(type);

      return renderSelect ? (
        <Select
          loading={!options}
          options={options}
          onChange={value => {
            onChange?.(value);
            if (type !== 'assetNumber') {
              handleCascadeFormReset(type, { [type]: value });
            }
          }}
          disabled={!options || (options && options?.length < 2)}
          style={{ width: '100%' }}
        />
      ) : (
        <Radio.Group
          onChange={({ target: { value } }) => {
            onChange?.(value);
            handleCascadeFormReset(type, { [type]: value });
          }}
          defaultValue={onlyOneOption ? options[0]?.value : null}
          disabled={onlyOneOption}
        >
          {options?.map(({ value, label }) => (
            <Radio.Button key={value} value={value}>
              {label}
            </Radio.Button>
          ))}
        </Radio.Group>
      );
    },
    [form, handleCascadeFormReset],
  );

  const handleColGrid = useCallback(
    (type: 'assetNumber' | TypeNames, options?: SelectionOptions[]) =>
      options?.length === 1 &&
      type !== 'assetNumber' &&
      !['complaintProblem', 'complaintReason'].includes(type)
        ? { sm: 12, xs: 24 }
        : { span: 24 },
    [],
  );

  const renderFormItem = useCallback(
    (
      type: 'assetNumber' | TypeNames,
      options: SelectionOptions[] | undefined,
    ) => {
      return (
        options && (
          <Col key={type} {...handleColGrid(type, options)}>
            <Form.Item
              name={type}
              label={t(type)}
              rules={[
                {
                  required: true,
                  message: t('msgs.requiredField'),
                },
              ]}
            >
              {renderInputEl(type, options)}
            </Form.Item>
          </Col>
        )
      );
    },
    [handleColGrid, renderInputEl, t],
  );

  const findNodeById = useCallback(
    (tree: TreeProps[] | undefined, id?: string): TreeProps | undefined =>
      id
        ? tree?.reduce<TreeProps | undefined>((found, node) => {
            if (found) return found;
            if (node.id === id) return node;
            return node.next ? findNodeById(node.next, id) : undefined;
          }, undefined)
        : undefined,
    [],
  );

  const mapToSelectionOptions = useCallback(
    (items: TreeProps[] | null | undefined): SelectionOptions[] | undefined =>
      items?.map(({ id, description }) => {
        const treatedDescription = `types.${description?.toLowerCase()}`;
        const hasTranslation = i18next.exists(
          `service-requests:${treatedDescription}`,
        );

        return {
          value: id,
          label: hasTranslation
            ? t(treatedDescription).toUpperCase()
            : description,
        };
      }),
    [t],
  );

  const renderFormInputs = useMemo(() => {
    const selectedType = findNodeById(
      typesTree?.roots,
      selectedValues?.srType1,
    );

    const defaultOptions: {
      srType1: SelectionOptions[] | undefined;
      srType2: SelectionOptions[] | undefined;
      srType3: SelectionOptions[] | undefined;
      srType4: SelectionOptions[] | undefined;
      complaintProblem: SelectionOptions[] | undefined;
      complaintReason: SelectionOptions[] | undefined;
    } = {
      srType1: mapToSelectionOptions(
        typesTree?.roots?.filter(n => n.description !== 'INFORMACAO'),
      ),
      srType2: mapToSelectionOptions(selectedType?.next),
      srType3: mapToSelectionOptions(
        findNodeById(typesTree?.roots, selectedValues?.srType2)?.next,
      ),
      srType4: undefined,
      complaintProblem: undefined,
      complaintReason: undefined,
    };

    if (selectedType?.description === 'SOLICITACAO') {
      defaultOptions.srType4 = mapToSelectionOptions(
        findNodeById(typesTree?.roots, selectedValues?.srType3)?.next,
      );
    } else if (selectedType?.description === 'RECLAMACAO') {
      defaultOptions.complaintProblem = mapToSelectionOptions(
        findNodeById(typesTree?.roots, selectedValues?.srType3)?.next,
      );
      defaultOptions.complaintReason = mapToSelectionOptions(
        findNodeById(typesTree?.roots, selectedValues?.complaintProblem)?.next,
      );
    }

    return Object.entries(defaultOptions)?.map(([type, values]) =>
      renderFormItem(type as TypeNames, values),
    );
  }, [
    findNodeById,
    mapToSelectionOptions,
    renderFormItem,
    selectedValues?.complaintProblem,
    selectedValues?.srType1,
    selectedValues?.srType2,
    selectedValues?.srType3,
    typesTree?.roots,
  ]);

  const normalizeDesc = useCallback(
    (desc: string) =>
      desc
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''),
    [],
  );

  const detailsConfig = useMemo((): { templateType: TemplateType } => {
    const raw = findNodeById(
      typesTree?.roots,
      selectedValues?.complaintReason,
    )?.description;
    const reasonDesc = raw ? normalizeDesc(raw) : undefined;

    if (!reasonDesc) return { templateType: null };
    if (reasonDesc === EQUIPAMENTO_KEY) return { templateType: 'equipamento' };
    if (isBloqueioKey(reasonDesc)) return { templateType: 'bloqueio' };
    if (reasonDesc === CONTESTACAO_FATURA_KEY)
      return { templateType: 'contestacaoFatura' };
    return { templateType: null };
  }, [
    findNodeById,
    normalizeDesc,
    selectedValues?.complaintReason,
    typesTree?.roots,
  ]);

  const computeWorklogText = useCallback(
    (values: Partial<FormValues>): string => {
      if (detailsConfig.templateType === 'equipamento') {
        return [
          `Quantidade de SIMCards: ${values?.eqQuantidade ?? ''}`,
          `Data do pedido: ${values?.eqDataPedido ?? ''}`,
          `Descrição: ${values?.eqDescricao ?? ''}`,
        ].join('\r\n');
      }
      if (detailsConfig.templateType === 'bloqueio') {
        return [
          `Número(s) da(s) linha(s) ou contrato: ${
            values?.blLinhasContrato ?? ''
          }`,
          `Descrição: ${values?.blDescricao ?? ''}`,
        ].join('\r\n');
      }
      if (detailsConfig.templateType === 'contestacaoFatura') {
        const formaDevolucaoLabel =
          FORMA_DEVOLUCAO_OPTIONS.find(
            o => o.value === values?.cfFormaDevolucao,
          )?.label ?? '';
        return [
          `Razão Social: ${values?.cfRazaoSocial ?? ''}`,
          `CNPJ: ${values?.cfCNPJ ?? ''}`,
          `Fatura: ${values?.cfFatura ?? ''}`,
          `Contrato/Asset Pai: ${values?.cfContrato ?? ''}`,
          `Produto: ${values?.cfProduto ?? ''}`,
          `Motivo da contestação: ${values?.cfMotivo ?? ''}`,
          `Valor contestado: ${values?.cfValor ?? ''}`,
          `Forma de devolução: ${formaDevolucaoLabel}`,
          `Nome: ${values?.cfRetornoNome ?? ''}`,
          `Confirmação do telefone: ${values?.cfRetornoTelefone ?? ''}`,
          `E-mail: ${values?.cfRetornoEmail ?? ''}`,
          `Consultor (e-mail): ${values?.cfRetornoConsultor ?? ''}`,
        ].join('\r\n');
      }
      return values?.details ?? '';
    },
    [detailsConfig.templateType],
  );

  const { currentTypedLength, maxTypedLength } = useMemo(() => {
    const values = selectedValues ?? {};
    const totalLength = computeWorklogText(values).length;

    let typedLength = 0;
    if (detailsConfig.templateType === 'equipamento') {
      typedLength =
        (values.eqQuantidade?.length ?? 0) +
        (values.eqDataPedido?.length ?? 0) +
        (values.eqDescricao?.length ?? 0);
    } else if (detailsConfig.templateType === 'bloqueio') {
      typedLength =
        (values.blLinhasContrato?.length ?? 0) +
        (values.blDescricao?.length ?? 0);
    } else if (detailsConfig.templateType === 'contestacaoFatura') {
      typedLength =
        (values.cfRazaoSocial?.length ?? 0) +
        (values.cfCNPJ?.length ?? 0) +
        (values.cfFatura?.length ?? 0) +
        (values.cfContrato?.length ?? 0) +
        (values.cfProduto?.length ?? 0) +
        (values.cfMotivo?.length ?? 0) +
        (values.cfValor?.length ?? 0) +
        (values.cfRetornoNome?.length ?? 0) +
        (values.cfRetornoTelefone?.length ?? 0) +
        (values.cfRetornoEmail?.length ?? 0) +
        (values.cfRetornoConsultor?.length ?? 0);
    }

    const overhead = totalLength - typedLength;
    return {
      currentTypedLength: typedLength,
      maxTypedLength: DETAILS_MAX_LENGTH - overhead,
    };
  }, [computeWorklogText, detailsConfig.templateType, selectedValues]);

  useEffect(() => {
    form.resetFields([
      'details',
      'eqQuantidade',
      'eqDataPedido',
      'eqDescricao',
      'blLinhasContrato',
      'blDescricao',
      'cfRazaoSocial',
      'cfCNPJ',
      'cfFatura',
      'cfContrato',
      'cfProduto',
      'cfMotivo',
      'cfValor',
      'cfFormaDevolucao',
      'cfRetornoNome',
      'cfRetornoTelefone',
      'cfRetornoEmail',
      'cfRetornoConsultor',
    ]);
  }, [detailsConfig.templateType, form]);

  const handleOnFinish = useCallback(
    async (formValues: FormValues) => {
      setRequestIsLoading(true);
      try {
        const rawDetails = computeWorklogText(formValues);

        if (rawDetails.length > DETAILS_MAX_LENGTH) {
          message.error(
            t('msgs.detailsMaxLength', { max: DETAILS_MAX_LENGTH }),
          );
          setRequestIsLoading(false);
          return;
        }

        const payload: NewServicePayload = {
          assetId: formValues?.assetNumber,
          worklogText: rawDetails,
          srTypeRequest: {
            srType1: formValues?.srType1,
            srType2: formValues?.srType2,
            srType3: formValues?.srType3,
            srType4: formValues?.srType4,
            complaintProblem: formValues?.complaintProblem,
            complaintReason: formValues?.complaintReason,
          },
        };

        await createServiceRequest(payload).then(async response => {
          if (response?.data?.serviceRequestId) {
            const file = formValues?.uploaded?.file;
            if (file) {
              const formData = new FormData();
              formData.append('file', file);
              try {
                await sendFile(
                  formData,
                  response.data.serviceRequestId,
                  formValues?.assetNumber,
                );
              } catch (e) {
                message.error(t('msgs.errorAttachingFile'));
              }
            }
          }
        });
        onSuccess?.();
        message.success(t('msgs.requestMadeSuccessfully'));
        handleCancel();
      } catch {
        message.error(t('msgs.errorWhenMakingRequest'));
      }
      setRequestIsLoading(false);
    },
    [
      computeWorklogText,
      createServiceRequest,
      handleCancel,
      onSuccess,
      sendFile,
      t,
    ],
  );

  return (
    <Modal
      title={t('newRequest')}
      open={open}
      onCancel={handleCancel}
      width={720}
      bodyStyle={{ padding: '24px 40px', overflowY: 'visible' }}
      destroyOnClose
      footer={false}
    >
      <Form form={form} layout="vertical" onFinish={handleOnFinish}>
        <Row gutter={[16, 0]}>
          {renderFormItem('assetNumber', contractOptions)}
          {renderFormInputs}
          {detailsConfig.templateType === 'equipamento' && (
            <>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="eqQuantidade"
                  label="Quantidade de SIMCards"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="eqDataPedido"
                  label="Data do pedido"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="eqDescricao"
                  label={t('details')}
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
              </Col>
            </>
          )}
          {detailsConfig.templateType === 'bloqueio' && (
            <>
              <Col span={24}>
                <Form.Item
                  name="blLinhasContrato"
                  label="Número(s) da(s) linha(s) ou contrato"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="blDescricao"
                  label={t('details')}
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
              </Col>
            </>
          )}
          {detailsConfig.templateType === 'contestacaoFatura' && (
            <>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfRazaoSocial"
                  label="Razão Social"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfCNPJ"
                  label="CNPJ"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfFatura"
                  label="Fatura"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfContrato"
                  label="Contrato/Asset Pai"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfProduto"
                  label="Produto"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfValor"
                  label="Valor contestado"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="cfMotivo"
                  label="Motivo da contestação"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="cfFormaDevolucao"
                  label="Forma de devolução"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Select
                    options={FORMA_DEVOLUCAO_OPTIONS}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfRetornoNome"
                  label="Nome para retorno"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfRetornoTelefone"
                  label="Telefone de confirmação"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfRetornoEmail"
                  label="E-mail para retorno"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="cfRetornoConsultor"
                  label="Consultor (e-mail)"
                  rules={[{ required: true, message: t('msgs.requiredField') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
          {detailsConfig.templateType === null && (
            <Col span={24}>
              <Form.Item
                name="details"
                label={t('details')}
                validateTrigger="onChange"
                rules={[
                  { required: true, message: t('msgs.requiredField') },
                  {
                    max: DETAILS_MAX_LENGTH,
                    message: t('msgs.detailsMaxLength', {
                      max: DETAILS_MAX_LENGTH,
                    }),
                  },
                ]}
              >
                <TextArea
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  showCount={{
                    formatter: ({ count }) =>
                      `${count} / ${DETAILS_MAX_LENGTH}`,
                  }}
                />
              </Form.Item>
            </Col>
          )}
          {detailsConfig.templateType !== null && (
            <Col span={24} style={{ marginBottom: 8 }}>
              <div style={{ textAlign: 'right' }}>
                <WorklogCounter
                  currentLength={currentTypedLength}
                  maxLength={maxTypedLength}
                />
              </div>
              {currentTypedLength > maxTypedLength && (
                <div style={{ textAlign: 'right' }}>
                  <Text type="danger">
                    {t('msgs.detailsMaxLength', { max: DETAILS_MAX_LENGTH })}
                  </Text>
                </div>
              )}
            </Col>
          )}
          <Col span={24}>
            <Form.Item name="uploaded">
              <Upload.Dragger maxCount={1} beforeUpload={() => false}>
                <S.UploaderContent>
                  <InboxOutlined />
                  <p>{t('msgs.uploadDescription')}</p>
                </S.UploaderContent>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end">
          <Divider />
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" loading={requestIsLoading}>
              {t('buttons.confirm')}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
};

export default NewServiceRequest;
